import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
  Overlay,
} from "react-bootstrap";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { FaLocationDot } from "react-icons/fa6";
import { FaCalendarCheck } from "react-icons/fa";
import { ShimmerContentBlock } from "react-shimmer-effects";

import "./styles/ForeClosure.css";
import { markInternalNavigation } from "@/utils/attributionCapture";
import {
  getActiveEventDates,
  getActiveEventsCount,
  getActiveEventState,
  getEventCalendarDetails,
} from "@/api/PDP";

import Header from "@/components/common/header/DefaultHeader";
import MetaComponent from "@/components/common/MetaComponent";
import PopupSignInUp from "@/components/common/PopupSignInUp";
import MobileMenu from "@/components/common/header/MobileMenu";
import backgroundImage from "./../../../../public/assets/images/background/cloud.jpg";
import OtherLinkFooter from "@/components/common/footer/OtherLinkFooter";
import NewFooter from "@/components/common/footer/NewFooter";

const metadata = {
  title: "Foreclosure | Events",
};

const fallbackValues = [[], [], null, 0]; // Fallback values for APIs

const ForeClosureListings = () => {
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  const [activeEventsCount, setActiveEventsCount] = useState(0);
  const [activeEventsState, setActiveEventsState] = useState(null);
  const [activeEventsDates, setActiveEventsDates] = useState(null);
  const [activeForeclosureEvents, setActiveForeclosureEvents] = useState([]);

  const [stateFilter, setStateFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isPagination, setIsPagination] = useState(false);

  const visibleCount = pageNumber * 10; // Dynamically set based on page number

  const handleShowMore = useCallback(() => {
    if (visibleCount < activeEventsCount) {
      setIsPagination(true);
      setPageNumber((prevPage) => prevPage + 1);
    }
  }, [visibleCount, activeEventsCount]);

  const handleClearFilters = () => {
    setPageNumber(1);
    setStateFilter("");
    setFromDate("");
    setToDate("");
  };

  const handleFromDateChange = (date) => {
    setPageNumber(1);
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    setFromDate(localDate.toLocaleDateString("en-CA"));
    setShowFromCalendar(false);
  };

  const handleToDateChange = (date) => {
    setPageNumber(1);
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    setToDate(localDate.toLocaleDateString("en-CA"));
    setShowToCalendar(false);
  };

  const handleStateFilter = (e) => {
    setPageNumber(1);
    setStateFilter(e.target.value);
  };

  const fetchAndProcessResults = async (apis, fallbackValues) => {
    const results = await Promise.allSettled(apis);
    return results.map((result, index) =>
      result.status === "fulfilled" && result.value !== null
        ? result.value
        : fallbackValues[index]
    );
  };

  const fetchData = useCallback(async (filters = {}, page) => {
    try {
      const { stateFilter, fromDate, toDate } = filters;
      const apis = [
        getEventCalendarDetails(stateFilter, fromDate, toDate, page),
        getActiveEventDates(stateFilter, fromDate, toDate),
        getActiveEventState(stateFilter, fromDate, toDate),
        getActiveEventsCount(stateFilter, fromDate, toDate),
      ];

      const [foreClosureEvents, dates, states, counts] =
        await fetchAndProcessResults(apis, fallbackValues);

      setActiveEventsCount(counts);
      setActiveEventsDates(dates);
      setActiveEventsState(states);
      setActiveForeclosureEvents((prevEvent) => {
        const newEvents = foreClosureEvents.eventList;
        return page === 1 ? newEvents : [...prevEvent, ...newEvents];
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsPagination(false);
    }
  }, []);

  useEffect(() => {
    fetchData({ stateFilter, fromDate, toDate }, pageNumber);
  }, [stateFilter, fromDate, toDate, pageNumber, fetchData]);

  const renderTooltip = useCallback(
    (text) => <Tooltip id="button-tooltip">{text}</Tooltip>,
    []
  );

  const formatMonthYear = useCallback((date) => {
    const options = { year: "numeric", month: "short" };
    let formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
    return formattedDate.split(" ").join("-");
  }, []);

  const isEventDate = useCallback(
    (date) => {
      const formattedDate = formatMonthYear(date);
      return activeEventsDates?.[formattedDate]?.includes(date.getDate());
    },
    [activeEventsDates, formatMonthYear]
  );

  return (
    <div className="listing-parent-container">
      <MetaComponent meta={metadata} />
      <Header />
      <MobileMenu />
      <PopupSignInUp />

      <Container className="listing-container">
        <Row className="row-filter-controls">
          <Col xs={12} md={3} className="p0 mt-3 mt-md-0 fc-filters">
            <Form.Select
              value={stateFilter}
              onChange={handleStateFilter}
              className="form-height w-100 fc-filter-text"
            >
              <option value="">Select State</option>
              {activeEventsState &&
                Object.entries(activeEventsState).map(([abbr, name]) => (
                  <option key={abbr} value={abbr}>
                    {name}
                  </option>
                ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={2} className="p0 mt-3 mt-md-0 fc-filters">
            <Form.Group className="position-relative">
              <Form.Control
                type="text"
                value={fromDate}
                placeholder="From"
                aria-label="From"
                readOnly
                ref={fromDateRef}
                onClick={() => setShowFromCalendar(!showFromCalendar)}
                className="form-height w-100 fc-filter-text"
              />
              <i
                className="bi bi-calendar position-absolute fc-icon"
                style={{
                  top: "50%",
                  right: "10px",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => setShowFromCalendar(!showFromCalendar)}
              ></i>
              <Overlay
                target={fromDateRef.current}
                show={showFromCalendar}
                placement="bottom"
                rootClose
                onHide={() => setShowFromCalendar(false)}
              >
                {({ placement, arrowProps, show: _show, popper, ...props }) => (
                  <div
                    {...props}
                    style={{
                      backgroundColor: "white",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      padding: "10px",
                      zIndex: 1050,
                      ...props.style,
                    }}
                  >
                    <Calendar
                      onChange={handleFromDateChange}
                      tileClassName={({ date }) =>
                        isEventDate(date) ? "event-date" : ""
                      }
                    />
                  </div>
                )}
              </Overlay>
            </Form.Group>
          </Col>
          <Col xs={12} md={2} className="p0 mt-3 mt-md-0 fc-filters">
            <Form.Group className="position-relative">
              <Form.Control
                type="text"
                value={toDate}
                placeholder="To"
                aria-label="To"
                readOnly
                ref={toDateRef}
                onClick={() => setShowToCalendar(!showToCalendar)}
                className="form-height w-100 fc-filter-text"
              />
              <i
                className="bi bi-calendar position-absolute fc-icon"
                style={{
                  top: "50%",
                  right: "10px",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => setShowToCalendar(!showToCalendar)}
              ></i>
              <Overlay
                target={toDateRef.current}
                show={showToCalendar}
                placement="bottom"
                rootClose
                onHide={() => setShowToCalendar(false)}
              >
                {({ placement, arrowProps, show: _show, popper, ...props }) => (
                  <div
                    {...props}
                    style={{
                      backgroundColor: "white",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      padding: "10px",
                      zIndex: 1050,
                      ...props.style,
                    }}
                  >
                    <Calendar
                      onChange={handleToDateChange}
                      tileClassName={({ date }) =>
                        isEventDate(date) ? "event-date" : ""
                      }
                    />
                  </div>
                )}
              </Overlay>
            </Form.Group>
          </Col>
          <Col xs={12} md={2} className="p0 mt-3 mt-md-0 fc-filters">
            <Button
              onClick={handleClearFilters}
              className="form-height w-100 fc-filter-text fc-clear-filter-btn d-flex align-items-center justify-content-center"
              style={{ outline: "none" }}
              variant="light"
            >
              <i className="bi bi-x me-2"></i>
              Clear All
            </Button>
          </Col>
        </Row>
        {isLoading ? (
          <div className="w-100 d-flex flex-column" style={{ gap: "24px" }}>
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                style={{
                  height: "110px",
                  overflow: "hidden",
                }}
              >
                <ShimmerContentBlock
                  text
                  thumbnailHeight={110}
                  thumbnailWidth={180}
                />
              </div>
            ))}
          </div>
        ) : activeForeclosureEvents?.length > 0 ? (
          <>
            <Row className="w-100">
              {activeForeclosureEvents.slice(0, visibleCount).map((event) => (
                <Col
                  md={12}
                  key={event.id}
                  className="mb-3 p-0 h-100 rounded-2"
                >
                  <Card className="shadow-sm border-light d-flex flex-md-row flex-column p-0 rounded-2">
                    {/* Hide header on smaller screens using Bootstrap's responsive display utilities */}
                    <Card.Header className="fixed-width-header position-relative p-0 d-none d-md-block rounded-start-2 overflow-hidden">
                      <div
                        className="position-relative w-100 h-100 rounded-start-2 overflow-hidden"
                        style={{
                          backgroundImage: `url(${backgroundImage})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        <div className="fc-blur-overlay"></div>
                        <div className="position-relative h-100">
                          {/* Country Info */}
                          <div className="fc-card-text-container">
                            <h5 className="fc-card-text">
                              {event.venueDetails.county}
                            </h5>
                            <p className="fc-card-text-sec">
                              {event.venueDetails.state}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card.Header>

                    {/* Address & Event Details */}
                    <Card.Body
                      className="position-relative card-body-background"
                      style={{
                        "--background-image": `url(${backgroundImage})`,
                      }}
                    >
                      {/* Mobile County/State Info */}
                      <div
                        className="d-md-none position-absolute"
                        style={{ top: "10px", right: "10px", zIndex: 2 }}
                      >
                        <div className="d-flex flex-column align-items-end">
                          <h5 className="text-white mb-2 fw-bold">
                            {event.venueDetails.county}
                          </h5>
                          <p className="text-white mb-0">
                            {event.venueDetails.state}
                          </p>
                        </div>
                      </div>
                      {/* Location & Date - Two Columns Layout */}
                      <div className="container-fluid p-0">
                        <div className="row p-0" style={{}}>
                          {/* Left Column: Where Section */}
                          <div className="col-12 col-md-5">
                            <div className="d-flex align-items-center gap-2 p-0">
                              <h5 className="fw-bold m-0 fc-text-white fc-event-text">
                                {event.venueDetails.eventName}
                              </h5>
                              {event.venueDetails.detailedEventAddress && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={renderTooltip(
                                    event.venueDetails.detailedEventAddress
                                  )}
                                >
                                  <i className="ms-1 bi bi-info-circle m-0 fc-bi-info-circle fc-text-white"></i>
                                </OverlayTrigger>
                              )}
                            </div>
                            <div className="d-flex pt-2">
                              <FaLocationDot className="me-2 fs-5 text-secondary fc-text-white fc-icon" />
                              <p className="small m-0 fc-text-white fc-event-text">
                                <strong>Where:</strong>{" "}
                                {event.venueDetails.eventAddress}
                              </p>
                            </div>
                          </div>

                          {/* Middle Column: When Section */}
                          <div className="col-12 col-md-4 d-flex">
                            <FaCalendarCheck className="me-2 fs-5 text-secondary fc-text-white fc-icon" />
                            <p className="small pt-1 fc-text-white fc-event-text">
                              <strong>
                                When: {new Date(event.eventDate).toDateString()}
                              </strong>
                            </p>
                          </div>

                          {/* Right Column: Button & Property Count */}
                          <div className="col-12 col-md-3 text-center text-md-end order-last order-md-0 event-btn-container">
                            <div className="d-flex flex-column flex-md-column align-items-center align-items-md-end">
                              <button
                                className="btn btn-sm btn-rounded border-1 border-secondary rounded-pill mb-2 fc-text-white event-btn"
                                onMouseOver={(e) => {
                                  e.target.classList.add("btn-thm");
                                  e.target.style.border =
                                    "1px solid var(--bs-secondary)";
                                }}
                                onMouseOut={(e) => {
                                  e.target.classList.remove("btn-thm");
                                  e.target.style.border =
                                    "1px solid var(--bs-secondary)";
                                }}
                                onClick={() => {
                                  const eventId = event?.id;
                                  const location =
                                    event?.venueDetails?.state?.toLowerCase();
                                  const url = `/foreclosure/${location}/auctions?eventId=${eventId}`;
                                  markInternalNavigation();
                                  window.open(url, "_blank");
                                }}
                              >
                                Event Details
                              </button>

                              {/* Property Count */}
                              <div className="mb-2 mb-md-0">
                                <p className="small mb-0 text-center text-md-end fc-text-white count-text">
                                  {event.propertyCount
                                    ? `${event.propertyCount} Propert${
                                        event.propertyCount === 1 ? "y" : "ies"
                                      }`
                                    : ""}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            {activeForeclosureEvents.length < activeEventsCount && (
              <Row className="w-100">
                <Col className="text-center p-0">
                  {isPagination ? (
                    <div
                      className="w-100 d-flex flex-column"
                      style={{ gap: "24px" }}
                    >
                      {[...Array(4)].map((_, index) => (
                        <div
                          key={index}
                          style={{
                            height: "110px",
                            overflow: "hidden",
                          }}
                        >
                          <ShimmerContentBlock
                            text
                            thumbnailHeight={110}
                            thumbnailWidth={180}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Button
                      variant="secondary fc-show-more-btn"
                      onClick={handleShowMore}
                    >
                      Show More ({activeEventsCount - visibleCount})
                    </Button>
                  )}
                </Col>
              </Row>
            )}
          </>
        ) : (
          <div className="text-center p-5 bg-light rounded shadow-sm">
            <i className="bi bi-calendar-x fs-1 text-muted mb-3"></i>
            <p className="lead mb-4">
              All of our foreclosure opportunities have been snapped up for
              today. Our inventory changes daily, so please check back soon.
            </p>
            <p className="text-muted">
              While you are waiting, take a look at all the other great deals we
              offer.
            </p>
            <hr className="my-4" />
            <Button
              variant="outline-primary"
              onClick={() => { markInternalNavigation(); window.open("/foreclosures", "_blank"); }}
            >
              Browse Other Properties <i className="bi bi-arrow-right ms-2"></i>
            </Button>
          </div>
        )}
      </Container>

      {/* Footer */}
      {/* <section className="footer_one home6 bg-ptrn2">
        <div className="container">
          <div className="row">
            <Footer />
          </div>
        </div>
      </section> */}
       <div className="container-fluid px-0">
              {/* <Footer /> */}
              <OtherLinkFooter />
              <NewFooter />
            </div>
    </div>
  );
};

export default ForeClosureListings;
